import { Color } from '@visiba/std';
import type { MappedTheme } from './_mapped_theme';

export class ThemeModel {
	public static createWithOldThemeVariables(oldTheme: Record<string, unknown>): ThemeModel {
		// Old theme ref (from dev1 / best):
		// c_accentColor: "#dc1a12"
		// c_backgroundBox: "#ffffff"
		// c_backgroundMain: "#ededed"
		// c_buttonDangerBackground: "#ad5454"
		// c_buttonDangerHoverBackground: "#9c4343"
		// c_buttonDangerHoverText: "#FFF"
		// c_buttonDangerText: "#FFF"
		// c_buttonDisabledBackground: "#a0a0a0"
		// c_buttonDisabledText: "#FFF"
		// c_buttonHoverBackground: "#7da839"
		// c_buttonHoverText: "#FFF"
		// c_buttonText: "#FFF"
		// c_errorBorder: "#ad5454"
		// c_errorText: "#8c0017"
		// c_inputBorder: "#a0a0a0"
		// c_inputPlaceholder: "#a0a0a0"
		// c_inputText: "#1a1a1a"
		// c_mainColorDark: "#649522"
		// c_mainColorDarkActive: "#314811"
		// c_mainColorLight: "#b2ca91"
		// c_mainOverlayBackground: "#000000c1"
		// c_textBody: "#1a1a1a"
		// c_textDark: "#333333"
		// c_textH1: "#1a1a1a"
		// c_textH2: "#1a1a1a"
		// c_textH3: "#1a1a1a"
		// c_textLight: "#ffffff"
		// c_textLink: "#00758e"
		// c_textLinkHover: "#016e85"
		// c_textTheme: "#649522"

		const builder: Record<keyof typeof MappedTheme, unknown> = {
			primary: oldTheme['c_mainColorDark'] ?? null, // This might not exist later // Carl
			topNavigationBarBackground: null,
			topNavigationBarText: null,
			bottomNavigationBarBackground: null,
			bottomNavigationBarActiveItem: null,
			bottomNavigationBarInactiveItem: null,
			webMainNavigationBarBackground: 'rgba(244, 246, 250, 0.7)',
			webMainNavigationBarActiveItem: oldTheme['c_textTheme'] ?? null,
			webMainNavigationBarInactiveItem: '#616e76',
			webMainNavigationBarHoverIndicator: (() => {
				const hex = oldTheme['c_textTheme'] as string;
				if (!hex) return null;

				const rgba = Color.createRgb(hex);
				rgba[3] = 0.1;

				return `rgba(${rgba.join(', ')})`;
			})(),
			background1a: oldTheme['c_backgroundMain'] ?? null,
			background1b: oldTheme['c_backgroundMain'] ?? null,
			textOnThemedBackground: null,
			secondaryTextInThemedBackground: null,
			baseButtonDefault: null,
			baseButtonHover: null,
			baseButtonClicked: null,
			iconButtonIcon: null,
			baseButtonText: null,
			iconButtonSecondaryText: null,
			baseButtonDisabled: null,
			baseButtonDisabledText: null,
			inputFieldBackground: null,
			inputFieldIcon: null,
			inputFieldText: null,
			inputFieldPlaceholderText: null,
			inputFieldBorder: null,
			background2: null,
			textOnBackground2: oldTheme['c_textLight'] ?? null,
			uiCardBackground: oldTheme['c_backgroundMain'] ?? null,
			uiCardText: oldTheme['c_textBody'] ?? null,
			uiCardSecondaryText: oldTheme['c_textBody'] ?? null,
			imageText: null,
			imageOverlay: null,
			textOnWhiteBackground: oldTheme['c_textBody'] ?? null,
			secondaryTextOnWhiteBackground: null,
			themedTextOnWhiteBackground: oldTheme['c_textTheme'] ?? null,
			primaryButtonDefault: oldTheme['c_mainColorDark'] ?? null,
			primaryButtonHover: oldTheme['c_buttonHoverBackground'] ?? null,
			primaryButtonClicked: oldTheme['c_mainColorDarkActive'] ?? null,
			primaryButtonText: oldTheme['c_buttonText'] ?? null,
			secondaryButtonDefault: null,
			secondaryButtonBorder: null,
			secondaryButtonHover: null,
			secondaryButtonClicked: null,
			secondaryButtonText: null,
			textButton: null,
			disabledButton: oldTheme['c_buttonDisabledBackground'] ?? null,
			disabledButtonText: oldTheme['c_buttonDisabledText'] ?? null,
			uiElements: null,
			hyperLinkDefault: oldTheme['c_textLink'] ?? null,
			hyperLinkHover: oldTheme['c_textLinkHover'] ?? null,
			hyperLinkClicked: oldTheme['c_textLink'] ?? null,
			chatBubble1: null,
			chatText1: null,
			chatBubble2: null,
			chatText2: null,
			selectedElement: null,
			hoveredElement: null,
			accentColor: oldTheme['c_accentColor'],
			systemColorSuccess: null,
			systemColorAttention: null,
			systemColorError: oldTheme['c_buttonDangerBackground'] ?? null,
			systemColorFocus: null,
		};

		return new ThemeModel(builder);
	}

	public static create(theme: Partial<Record<keyof typeof MappedTheme, string>>): ThemeModel {
		return new ThemeModel(theme);
	}

	private constructor(public readonly theme: Record<string, unknown>) {
		// Empty
	}
}
